import { createContext, useContext, useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router";
import { getTemplateMeta } from "../../utils/templateApi";

/**
 * TemplateContext provides the current template's ID, type, and path utilities.
 */

export type TemplateType = "branding" | "store" | "facelift";

interface TemplateContextType {
  templateId: string;
  templateType: TemplateType;
  isMaster: boolean;
  isLoadingMeta: boolean;
  sitePath: (path: string) => string;
  relativePath: string;
}

const TemplateContext = createContext<TemplateContextType | null>(null);

export function TemplateProvider({ children }: { children: React.ReactNode }) {
  const { templateId: paramId } = useParams<{ templateId: string }>();
  const { pathname } = useLocation();

  const templateId = paramId || "default";
  const prefix = `/t/${templateId}`;

  const [templateType, setTemplateType] = useState<TemplateType>("branding");
  const [isMaster, setIsMaster] = useState(false);
  const [isLoadingMeta, setIsLoadingMeta] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;
    setIsLoadingMeta(true);

    getTemplateMeta(templateId).then((meta) => {
      if (cancelled || !mountedRef.current) return;
      if (meta?.templateType) {
        setTemplateType(meta.templateType as TemplateType);
      } else {
        setTemplateType("branding");
      }
      setIsMaster(!!meta?.isMaster);
      setIsLoadingMeta(false);
    }).catch(() => {
      if (cancelled || !mountedRef.current) return;
      setTemplateType("branding");
      setIsMaster(false);
      setIsLoadingMeta(false);
    });

    return () => {
      cancelled = true;
      mountedRef.current = false;
    };
  }, [templateId]);

  const sitePath = (path: string): string => {
    if (path.startsWith("/")) return `${prefix}${path}`;
    return path;
  };

  const relativePath = pathname.startsWith(prefix)
    ? pathname.slice(prefix.length) || "/"
    : pathname;

  return (
    <TemplateContext.Provider value={{ templateId, templateType, isMaster, isLoadingMeta, sitePath, relativePath }}>
      {children}
    </TemplateContext.Provider>
  );
}

export function useTemplate() {
  const ctx = useContext(TemplateContext);
  if (!ctx) throw new Error("useTemplate must be used within TemplateProvider");
  return ctx;
}

export function useTemplateSafe() {
  return useContext(TemplateContext);
}

export function useTemplateId(): string {
  const ctx = useContext(TemplateContext);
  return ctx?.templateId || "default";
}

export function useTemplateType(): TemplateType {
  const ctx = useContext(TemplateContext);
  return ctx?.templateType || "branding";
}

export function useIsMaster(): boolean {
  const ctx = useContext(TemplateContext);
  return ctx?.isMaster || false;
}

export function useSitePath(): (path: string) => string {
  const ctx = useContext(TemplateContext);
  if (!ctx) return (path: string) => path;
  return ctx.sitePath;
}

export function useRelativePath(): string {
  const ctx = useContext(TemplateContext);
  return ctx?.relativePath || "/";
}
